'use client'

import { cn } from '@/utilities/cn'
import { createUrl } from '@/utilities/createUrl'
import { SearchIcon, Loader2, X } from 'lucide-react'
import { Link, useRouter, usePathname } from '@/i18n/routing'
import { useSearchParams } from 'next/navigation'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Price } from '@/components/Price'
import { useTranslations, useLocale } from 'next-intl'

type SuggestionItem = {
  id: number
  title: string
  slug: string
  price: number | null
}

type Variant = 'dark' | 'light'

type Props = {
  className?: string
  onSelect?: () => void
  /**
   * Visual variant.
   * - 'dark' (default): semi-transparent black glass with neon-orange focus,
   *   designed to sit on dark photographic backgrounds (header bar, hero).
   * - 'light': solid white with dark text, no backdrop-blur, no uppercase/mono,
   *   designed for surfaces with `bg-background` (white) — e.g. the MobileMenu
   *   drawer. Subtle sky focus ring instead of neon orange.
   */
  variant?: Variant
}

export const Search: React.FC<Props> = ({ className, onSelect, variant = 'dark' }) => {
  const t = useTranslations('search')
  const locale = useLocale()
  const router = useRouter()
  const searchParams = useSearchParams()
  const [query, setQuery] = useState(searchParams?.get('q') || '')
  const [results, setResults] = useState<SuggestionItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const isLight = variant === 'light'

  const fetchSuggestions = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.trim().length < 2) {
        setResults([])
        return
      }

      setIsLoading(true)
      try {
        const params = new URLSearchParams({
          q: searchQuery.trim(),
          limit: '4',
        })

        const response = await fetch(`/api/search/suggest?${params.toString()}`)
        if (response.ok) {
          const data = await response.json()
          setResults(data.products || [])
        }
      } catch (error) {
        console.error('Search error:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim()) {
        fetchSuggestions(query)
      } else {
        setResults([])
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query, fetchSuggestions])

  useEffect(() => {
    setMounted(true)
  }, [])

  // Compute dropdown position from the input's bounding rect. The dropdown is
  // portaled to document.body so it can escape overflow:auto ancestors (e.g.
  // MobileMenu's SheetContent, which has overflow-y-auto).
  const updateDropdownPos = useCallback(() => {
    if (!inputRef.current) return
    const rect = inputRef.current.getBoundingClientRect()
    setDropdownPos({
      top: rect.bottom + 8, // 8px = mt-2
      left: rect.left,
      width: rect.width,
    })
  }, [])

  useEffect(() => {
    if (showSuggestions) {
      updateDropdownPos()
      window.addEventListener('scroll', updateDropdownPos, true)
      window.addEventListener('resize', updateDropdownPos)
      return () => {
        window.removeEventListener('scroll', updateDropdownPos, true)
        window.removeEventListener('resize', updateDropdownPos)
      }
    }
  }, [showSuggestions, updateDropdownPos])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const insideInput = containerRef.current?.contains(target)
      const insideDropdown = dropdownRef.current?.contains(target)
      if (!insideInput && !insideDropdown) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const newParams = new URLSearchParams(searchParams?.toString())

    if (query) {
      newParams.set('q', query)
    } else {
      newParams.delete('q')
    }

    setShowSuggestions(false)
    onSelect?.()
    router.push(createUrl('/shop', newParams))
  }

  const handleClear = () => {
    setQuery('')
    setResults([])
    setShowSuggestions(false)
    const newParams = new URLSearchParams(searchParams?.toString())
    newParams.delete('q')
    router.push(createUrl('/shop', newParams))
  }

  const hasMatches = showSuggestions && results.length > 0

  // ─── Variant-aware styling ──────────────────────────────────────────────

  const inputClass = isLight
    ? // Light variant: white card on white drawer bg. Visible border, dark
      // text, normal case, no monospace, no backdrop-blur. Subtle sky focus.
      cn(
        'w-full rounded-2xl border bg-white border-neutral-300 px-5 py-3 text-base text-neutral-900',
        'placeholder:text-neutral-500 outline-none pr-14 hover:border-neutral-400',
        'focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 transition-all shadow-sm',
      )
    : // Dark variant: glass card on dark photo bg. Original hero aesthetic.
      cn(
        'w-full rounded-2xl border bg-black/40 backdrop-blur-xl px-6 py-3 text-base font-mono tracking-widest uppercase text-white',
        'placeholder:text-neutral-400 placeholder:uppercase outline-none pr-14 border-white/25',
        'focus:bg-black/50 focus:border-[var(--neon-orange)]/60 focus:ring-2 focus:ring-[var(--neon-orange)]/30 focus:shadow-[0_0_25px_-5px_var(--neon-orange)] transition-all shadow-[0_4px_20px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.1)]',
      )

  const iconClass = isLight ? 'h-5 w-5 text-neutral-500' : 'h-5 w-5 text-neutral-400'
  const clearIconClass = isLight
    ? 'h-5 w-5 text-neutral-500 group-hover:text-neutral-900 transition-colors'
    : 'h-5 w-5 text-neutral-400 group-hover:text-sky-300 transition-colors'
  const loaderClass = isLight ? 'h-5 w-5 animate-spin text-sky-500' : 'h-5 w-5 animate-spin text-sky-400'
  const clearButtonClass = isLight
    ? 'group transition-colors rounded-full p-1 hover:bg-neutral-100'
    : 'group transition-colors'

  const dropdownClass = isLight
    ? 'w-full overflow-hidden rounded-2xl border bg-white border-neutral-200 shadow-xl'
    : 'w-full overflow-hidden rounded-2xl border bg-black/80 backdrop-blur-2xl shadow-2xl'

  const dropdownBorderClass = isLight
    ? 'border-neutral-200'
    : hasMatches
      ? 'border-sky-400/60 shadow-[0_10px_40px_rgba(56,189,248,0.25)]'
      : 'border-white/20'

  const itemClass = isLight
    ? 'flex items-center gap-3 px-4 py-3 hover:bg-sky-50 transition-colors group border-l-2 border-transparent hover:border-sky-500'
    : 'flex items-center gap-3 px-4 py-3 hover:bg-sky-500/10 transition-colors group border-l-2 border-transparent hover:border-sky-400'

  const itemTitleClass = isLight
    ? 'text-sm font-medium truncate text-neutral-900 group-hover:text-sky-700 transition-colors'
    : 'text-sm font-medium truncate text-neutral-200 group-hover:text-sky-300 transition-colors'

  const itemPriceClass = isLight ? 'text-xs text-neutral-500' : 'text-xs text-neutral-400'

  const seeAllClass = isLight
    ? 'w-full px-4 py-3 text-left text-sm text-sky-700 hover:bg-sky-50 hover:text-sky-800 transition-colors'
    : 'w-full px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-neutral-400 hover:text-sky-300 transition-colors'

  const emptyHintClass = isLight
    ? 'p-4 text-center text-sm text-neutral-500'
    : 'p-4 text-center text-xs font-mono text-neutral-400'

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <form onSubmit={onSubmit} className="relative">
        <input
          ref={inputRef}
          autoComplete="off"
          className={inputClass}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setShowSuggestions(true)
          }}
          onFocus={() => setShowSuggestions(true)}
          name="search"
          placeholder={t('placeholder')}
          type="text"
        />
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {query ? (
            <button
              onClick={handleClear}
              type="button"
              className={clearButtonClass}
              aria-label="Clear search"
            >
              <X className={clearIconClass} />
            </button>
          ) : isLoading ? (
            <Loader2 className={loaderClass} />
          ) : (
            <SearchIcon className={cn(iconClass, hasMatches && (isLight ? 'text-sky-500' : 'text-sky-400'))} />
          )}
        </div>
      </form>

      {mounted && showSuggestions && dropdownPos && (results.length > 0 || isLoading || query.trim().length >= 2) &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 100,
            }}
            className={cn(
              dropdownClass,
              dropdownBorderClass,
              'animate-in fade-in slide-in-from-top-2 duration-200',
            )}
          >
            {isLoading && results.length === 0 ? (
              <div className={emptyHintClass}>{t('searching')}</div>
            ) : results.length === 0 ? (
              <div className={emptyHintClass}>{t('noResultsHint')}</div>
            ) : (
              <ul className="py-2">
                {results.map((product: SuggestionItem) => (
                  <li key={product.id}>
                    <Link
                      href={`/products/${product.slug}`}
                      onClick={() => {
                        setIsNavigating(true)
                        setShowSuggestions(false)
                        onSelect?.()
                      }}
                      className={itemClass}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={itemTitleClass}>{product.title}</p>
                        {typeof product.price === 'number' && (
                          <p className={itemPriceClass}>
                            <Price amount={product.price} as="span" />
                          </p>
                        )}
                      </div>
                      <div
                        className={cn(
                          isLight
                            ? 'text-sky-700 opacity-0 group-hover:opacity-100 transition-opacity text-xs'
                            : 'text-sky-400 opacity-0 group-hover:opacity-100 transition-opacity text-xs font-mono',
                        )}
                      >
                        {t('view')} →
                      </div>
                    </Link>
                  </li>
                ))}
                <li className={isLight ? 'border-t border-neutral-200 mt-2' : 'border-t border-white/10 mt-2'}>
                  <button
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams?.toString())
                      newParams.set('q', query)
                      router.push(createUrl('/shop', newParams))
                      setShowSuggestions(false)
                      onSelect?.()
                    }}
                    className={seeAllClass}
                  >
                    {t('seeAll', { query })}
                  </button>
                </li>
              </ul>
            )}
          </div>,
          document.body,
        )}

      {/* Navigation loading overlay */}
      {isNavigating && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-12 w-12 animate-spin text-[var(--neon-orange)]" />
            <p className="text-sm font-mono text-white/70">Loading...</p>
          </div>
        </div>
      )}
    </div>
  )
}
